import { BigNumber } from '@ethersproject/bignumber';
import { formatUnits, parseUnits } from '@ethersproject/units';
import { Instructions, LTV, mapToCollateralAsset, mapToDebtAsset, reachHealthFactor } from 'app/features/util/ltv';
import { DecoratedSilo, getCollateralAssets, getDebtAssets, SiloAsset } from 'queries/decoratedSilo';
import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';
import { DualVariantSlider } from './DualVariantSlider';
import { GetNewLTV } from './types';
import * as Option from 'fp-ts/Option';
import { constFalse, constUndefined, pipe } from 'fp-ts/lib/function';
import { BaseHealthFactor } from './components';
import { PERCENT_DECIMALS } from '@silo/types';
import { normalizeToPoints_LEGACY } from '@silo/utils';

export type DualVariantProps = {
  variant: 'dual';
  normalizedTokenAmount: BigNumber | null;
  selectedAsset: SiloAsset;
  currentLTV: LTV;
  getNewLTV: GetNewLTV;
  decoratedSilo: DecoratedSilo;
  setTokenAmount: Dispatch<SetStateAction<string>>;
  setMaxAmount: () => void;
  healthFactorConfig?: HealthFactorConfig;
  disabled?: boolean;
};

export interface HealthFactorConfig {
  getTokenAdjustment?: (instructions: Instructions) => bigint;
  getMin?: GetLimit;
  getMax?: GetLimit;
  setMaxAmountWhen?: (params: { value: number; LTV: LTV }) => boolean;
}

const defaultHealthConfig: Required<HealthFactorConfig> = {
  getTokenAdjustment: ({ tokens }) => tokens,
  getMin: constUndefined,
  getMax: constUndefined,
  setMaxAmountWhen: constFalse,
};

export const DualVariant: FC<DualVariantProps> = ({
  normalizedTokenAmount,
  selectedAsset,
  currentLTV,
  getNewLTV,
  decoratedSilo,
  setTokenAmount,
  setMaxAmount,
  healthFactorConfig,
  disabled,
}) => {
  const { getTokenAdjustment, getMin, getMax, setMaxAmountWhen } = {
    ...defaultHealthConfig,
    ...healthFactorConfig,
  };

  const newLTV = useMemo(() => {
    if (!normalizedTokenAmount) return currentLTV;

    return getNewLTV({
      currentLTV,
      normalizedTokenAmount,
      selectedAsset,
    });
  }, [normalizedTokenAmount, selectedAsset, currentLTV, getNewLTV]);

  const newHealthFactorPoints = normalizeToPoints_LEGACY(newLTV.healthFactor);
  const currentRiskFactorPoints = normalizeToPoints_LEGACY(currentLTV.riskFactor);
  const riskFactorPoints = normalizeToPoints_LEGACY(newLTV.riskFactor);

  const min = determineLimit(currentLTV, getMin);
  const max = determineLimit(currentLTV, getMax);

  const setRiskFactorPoints = useCallback(
    (value: number) => {
      if (value === min || value === max) {
        return setTokenAmount('');
      }

      if (setMaxAmountWhen({ LTV: currentLTV, value })) {
        return setMaxAmount();
      }

      const collateralAssets = getCollateralAssets(decoratedSilo);
      const debtAssets = getDebtAssets(decoratedSilo);

      // subtracting 2 since value is points vs. a percentage (i.e. `50` should be intepreted as `0.50`)
      const healthFactor = parseUnits(`${100 - value}`, PERCENT_DECIMALS - 2).toBigInt();

      const instructions = reachHealthFactor({
        collateralAssets: collateralAssets.map(mapToCollateralAsset),
        debtAssets: debtAssets.map(mapToDebtAsset),
        healthFactor,
        asset: selectedAsset,
      });

      const tokens = getTokenAdjustment(instructions);

      setTokenAmount(tokens === 0n ? '' : formatUnits(tokens, instructions.decimals));
    },
    [
      min,
      max,
      currentLTV,
      decoratedSilo,
      setTokenAmount,
      setMaxAmountWhen,
      setMaxAmount,
      selectedAsset,
      getTokenAdjustment,
    ]
  );

  return (
    <BaseHealthFactor
      displayPoints={newHealthFactorPoints}
      disabled={disabled}
      bar={
        <DualVariantSlider
          currentRiskFactorPoints={currentRiskFactorPoints}
          riskFactorPoints={riskFactorPoints}
          setRiskFactorPoints={setRiskFactorPoints}
          min={min}
          max={max}
          disabled={disabled}
        />
      }
    />
  );
};

const sliderMin = 0;
const sliderMax = 100;
const withinSliderLimit = (value: number) => (value < sliderMin ? sliderMin : value > sliderMax ? sliderMax : value);

type GetLimit = (LTV: LTV) => bigint | undefined;

const determineLimit = (LTV: LTV, getLimit: GetLimit) =>
  pipe(
    LTV,
    getLimit,
    Option.fromNullable,
    Option.map(normalizeToPoints_LEGACY),
    Option.map(withinSliderLimit),
    Option.toUndefined
  );
